/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Month-grid maths for the date picker.
 *
 * Kept free of React and of the agenda feature so the shared input can use it
 * without depending on `features/agenda` — the agenda has its own copy tuned
 * to a full-page grid (it renders leading/trailing days as blanks and needs
 * the `isCurrentMonth` flag); this one only ever renders the current month.
 */

/** One day cell in the picker grid. `null` is a leading/trailing blank. */
export type DayCell = { iso: string; day: number } | null;

/**
 * ISO `yyyy-MM-dd` for a local date, without timezone drift.
 */
export const toISODate = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Parses ISO `yyyy-MM-dd` as a local date, or null when unparseable.
 */
export const fromISODate = (iso: string | undefined | null): Date | null => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Builds the cells for one month, week-aligned on Sunday.
 *
 * Leading and trailing positions are `null` rather than neighbouring days: the
 * picker is for choosing a date in the month on screen, and offering a day
 * that also lives in the previous month invites off-by-one selections.
 *
 * @param year - Full year.
 * @param month - Zero-based month index.
 * @returns Cells in render order, always a whole number of weeks.
 */
export const monthCells = (year: number, month: number): DayCell[] => {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: DayCell[] = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ iso: toISODate(new Date(year, month, day)), day });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/**
 * Whether a cell falls outside the allowed range.
 *
 * Compares ISO strings, which sort chronologically, so no Date is constructed
 * just to answer this.
 *
 * @param iso - The cell's date.
 * @param min - Inclusive lower bound, ISO or undefined.
 * @param max - Inclusive upper bound, ISO or undefined.
 * @returns True when the date may not be selected.
 */
export const isOutOfRange = (
  iso: string,
  min?: string,
  max?: string,
): boolean => Boolean((min && iso < min) || (max && iso > max));

/**
 * Shifts a year/month pair by whole months, rolling the year over.
 */
export const shiftMonth = (
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } => {
  const shifted = new Date(year, month + delta, 1);
  return { year: shifted.getFullYear(), month: shifted.getMonth() };
};
